import { useCallback, useEffect, useState } from 'react';
import { regenererPdfDevis, telechargerPdfDevis, urlPdfDevis } from '../services/devisService';
import {
  getContratParDevis,
  regenererPdfContrat,
  telechargerPdfContrat,
  urlPdfContrat,
} from '../services/contratService';
import { enregistrerNotes } from '../services/contactService';
import {
  deposerDocumentContact,
  formatTaille,
  getDocumentsContact,
  supprimerDocument,
  renommerDocument,
  urlFichierDocument,
} from '../services/documentClientService';
import SessionsPanel from './SessionsPanel';
import EmailsEnvoyes from './EmailsEnvoyes';
import HistoriqueStatuts from '../components/HistoriqueStatuts';
import { STATUT_LABELS, formatDateTime, formatMontant, STATUT_DEVIS_LABELS, formaterTelephone } from './devisUtils';
import SelectRecherche from '../components/SelectRecherche';
import NomEditable from './NomEditable';
import RemplirParTexte from '../components/RemplirParTexte';
import '../assets/styles/DevisPanel.css';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/Clients.css';
import './Signature.css';
import {
  CATEGORIES_DOCUMENT,
  FORMATS_ACCEPTES,
  LIBELLE_CATEGORIE,
} from './categoriesDocument';
import { IconeOeil, IconeTelecharger } from '../components/IconesDocument';

// Les natures de fichier vivent à part : la saisie manuelle d'une demande
// propose le même choix, et les deux écrans ne doivent pas diverger.
const CATEGORIES = CATEGORIES_DOCUMENT;

/**
 * Fiche d'une demande de devis, ouverte au clic sur sa ligne.
 *
 * Une modale à onglets, comme les panneaux « Voir » des autres listes :
 * la fiche portait auparavant six sections empilées dans un tiroir, qu'il
 * fallait parcourir au défilement pour trouver l'historique.
 */
function ContactDetailPanel({
  contact,
  loading,
  error,
  onClose,
  devis,
  devisLoading,
  generation,
  generationErreur,
  onGenererDevis,
  onVoirDevis,
}) {
  const historique = [...(contact?.historique || [])].sort(
    (a, b) => new Date(b.dateMaj) - new Date(a.dateMaj)
  );
  const dernierStatut = historique[0]?.statut;

  const [onglet, setOnglet] = useState('demande');
  const [pdfEnCours, setPdfEnCours] = useState(false);
  const [pdfErreur, setPdfErreur] = useState(null);
  const [pdfInfo, setPdfInfo] = useState(null);
  const [regenerationEnCours, setRegenerationEnCours] = useState(false);

  // Contrat de formation rattaché au devis affiché. Il est produit
  // automatiquement dès que le client signe le devis (aucune action
  // manuelle côté back-office) : ce panneau ne fait qu'en afficher l'état.
  const [contrat, setContrat] = useState(null);
  const [contratLoading, setContratLoading] = useState(false);
  const [contratErreur, setContratErreur] = useState(null);
  const [contratPdfEnCours, setContratPdfEnCours] = useState(false);
  // Panneau « Sessions & documents » : convocations et feuilles de présence.
  const [sessionsOuvertes, setSessionsOuvertes] = useState(false);

  /* ---------------- Notes ---------------- */

  const [notes, setNotes] = useState('');
  const [notesEnregistrees, setNotesEnregistrees] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesMessage, setNotesMessage] = useState(null);

  useEffect(() => {
    const valeur = contact?.notes || '';
    setNotes(valeur);
    setNotesEnregistrees(valeur);
    setNotesMessage(null);
  }, [contact?.id, contact?.notes]);

  // Le devis parti fige les notes : les enrichir ne changerait plus le
  // document, déjà écrit et chez le client. Le serveur tranche (409) ;
  // l'écran ne fait que ne pas proposer l'inutile.
  const devisParti = Boolean(devis?.date_envoi);
  const notesModifiees = notes !== notesEnregistrees;

  async function sauverNotes() {
    setNotesSaving(true);
    setNotesMessage(null);
    try {
      await enregistrerNotes(contact.id, notes);
      setNotesEnregistrees(notes);
      setNotesMessage({ ok: true, texte: 'Notes enregistrées.' });
    } catch (err) {
      setNotesMessage({ ok: false, texte: err.message || 'Enregistrement impossible.' });
    } finally {
      setNotesSaving(false);
    }
  }

  /* ---------------- Documents ---------------- */

  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [depot, setDepot] = useState({ categorie: 'devis', libelle: '' });
  const [depotEnCours, setDepotEnCours] = useState(false);
  const [depotMessage, setDepotMessage] = useState(null);

  const chargerDocuments = useCallback(() => {
    if (!contact?.id) return undefined;
    setDocsLoading(true);
    return getDocumentsContact(contact.id)
      .then((res) => setDocuments(res.data || []))
      .catch(() => setDocuments([]))
      .finally(() => setDocsLoading(false));
  }, [contact?.id]);

  useEffect(() => {
    chargerDocuments();
  }, [chargerDocuments]);

  async function deposerFichier(e) {
    const fichier = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!fichier) return;

    setDepotEnCours(true);
    setDepotMessage(null);
    try {
      await deposerDocumentContact(contact.id, fichier, {
        categorie: depot.categorie,
        libelle: depot.libelle.trim() || undefined,
        devisId: devis?.id,
      });
      setDepot((d) => ({ ...d, libelle: '' }));
      setDepotMessage({ ok: true, texte: `${fichier.name} ajouté.` });
      await chargerDocuments();
    } catch (err) {
      setDepotMessage({ ok: false, texte: err.message || 'Dépôt impossible.' });
    } finally {
      setDepotEnCours(false);
    }
  }

  // Pièce dont on édite le nom sur place (null : aucune).
  const [renommageId, setRenommageId] = useState(null);

  async function renommerFichier(doc, nom) {
    await renommerDocument(doc.id, nom);
    await chargerDocuments();
  }

  async function retirerFichier(doc) {
    setDepotEnCours(true);
    try {
      await supprimerDocument(doc.id);
      await chargerDocuments();
    } catch (err) {
      setDepotMessage({ ok: false, texte: err.message || 'Suppression impossible.' });
    } finally {
      setDepotEnCours(false);
    }
  }

  /* ---------------- Contrat ---------------- */

  // Le contrat suit le devis : changer de demande (ou générer un devis)
  // relance sa lecture. Son absence n'est pas une erreur.
  const devisId = devis?.id ?? null;

  useEffect(() => {
    if (devisId == null) {
      setContrat(null);
      return undefined;
    }

    let annule = false;
    setContratLoading(true);
    setContratErreur(null);

    getContratParDevis(devisId)
      .then((res) => {
        if (!annule) setContrat(res.data || null);
      })
      .catch((err) => {
        if (!annule) setContratErreur(err.message || 'Contrat illisible.');
      })
      .finally(() => {
        if (!annule) setContratLoading(false);
      });

    return () => {
      annule = true;
    };
  }, [devisId]);

  const nomPdf = devis?.pdf_path ? devis.pdf_path.split('/').pop() : null;
  // Avant sa validation, le document est une estimation (il devient le devis à la validation).
  const estimation = Boolean(devis) && !devis.valide_le && ['brouillon', 'a_valider'].includes(devis.statut);

  /** Refait le PDF du devis (ou du contrat) depuis les données actuelles de la fiche. */
  async function regenerer(cible) {
    setRegenerationEnCours(true);
    setPdfErreur(null);
    setContratErreur(null);
    setPdfInfo(null);
    try {
      const res = cible === 'contrat' ? await regenererPdfContrat(contrat.id) : await regenererPdfDevis(devis.id);
      setPdfInfo(res.message || 'PDF refait.');
    } catch (err) {
      (cible === 'contrat' ? setContratErreur : setPdfErreur)(err.message || 'Régénération impossible.');
    } finally {
      setRegenerationEnCours(false);
    }
  }

  function voirPdf() {
    window.open(urlPdfDevis(devis.id, { forcer: true, apercu: true }), '_blank', 'noopener');
  }

  async function telechargerPdf() {
    setPdfEnCours(true);
    setPdfErreur(null);
    try {
      await telechargerPdfDevis(devis.id);
    } catch (err) {
      setPdfErreur(err.message || 'Téléchargement impossible.');
    } finally {
      setPdfEnCours(false);
    }
  }

  const nomPdfContrat = contrat?.pdf_path ? contrat.pdf_path.split('/').pop() : null;

  function voirContrat() {
    window.open(urlPdfContrat(contrat.id, { forcer: true, apercu: true }), '_blank', 'noopener');
  }

  async function telechargerContrat() {
    setContratPdfEnCours(true);
    setContratErreur(null);
    try {
      await telechargerPdfContrat(contrat.id, { forcer: true });
    } catch (err) {
      setContratErreur(err.message || 'Téléchargement impossible.');
    } finally {
      setContratPdfEnCours(false);
    }
  }

  /* ---------------- Onglets ---------------- */

  const ONGLETS = [
    { cle: 'demande', label: 'Demande' },
    { cle: 'notes', label: 'Notes', point: Boolean(notesEnregistrees) },
    { cle: 'devis', label: 'Devis', compte: devis ? 1 : 0 },
    { cle: 'contrat', label: 'Contrat', compte: contrat ? 1 : 0 },
    { cle: 'documents', label: 'Documents', compte: documents.length },
    { cle: 'emails', label: 'Emails' },
    { cle: 'historique', label: 'Historique', compte: historique.length },
  ];

  return (
    <div className="devis-modal-overlay" onClick={onClose}>
      <div className="devis-modal" onClick={(e) => e.stopPropagation()}>
        <div className="devis-modal-header">
          <div>
            <div className="devis-modal-numero">
              {contact
                ? [contact.prenom, contact.nom].filter(Boolean).join(' ') ||
                  contact.societeNom ||
                  contact.email ||
                  'Demande'
                : 'Demande'}
            </div>
            <div className="devis-modal-sub">{contact?.societeNom}</div>
          </div>
          <div className="devis-modal-header-right">
            {dernierStatut && (
              <span className="devis-status-pill devis-status-pill-large">
                {STATUT_LABELS[dernierStatut] || dernierStatut}
              </span>
            )}
            <button
              type="button"
              className="devis-drawer-close"
              onClick={onClose}
              aria-label="Fermer"
            >
              ×
            </button>
          </div>
        </div>

        {loading && <div className="devis-state">Chargement de la demande...</div>}
        {!loading && error && <div className="devis-state devis-state-error">{error}</div>}

        {!loading && !error && contact && (
          <>
            <nav className="clients-onglets contact-onglets">
              {ONGLETS.map((o) => (
                <button
                  key={o.cle}
                  type="button"
                  className={'clients-onglet' + (onglet === o.cle ? ' clients-onglet-actif' : '')}
                  onClick={() => setOnglet(o.cle)}
                >
                  {o.label}
                  {o.compte > 0 && <span className="clients-onglet-compte">{o.compte}</span>}
                  {o.point && <span className="contact-onglet-point" aria-hidden="true" />}
                </button>
              ))}
            </nav>

            <div className="devis-modal-body">
              {/* ---------------- Demande ---------------- */}
              {onglet === 'demande' && (
                <>
                  <div className="devis-drawer-section-title">Coordonnées</div>
                  <dl className="devis-drawer-grid">
                    <dt>Email</dt>
                    <dd>{contact.email}</dd>
                    <dt>Téléphone</dt>
                    <dd>{formaterTelephone(contact.numeroTelephone) || '—'}</dd>
                    <dt>Effectif société</dt>
                    <dd>{contact.effectifSociete || '—'}</dd>
                    <dt>Nombre d’élus</dt>
                    <dd>{contact.nbElus || '—'}</dd>
                    <dt>Site web</dt>
                    <dd>{contact.siteWeb || '—'}</dd>
                    <dt>Créneau de rappel</dt>
                    <dd>{contact.creneauRappel || '—'}</dd>
                    <dt>Reçue le</dt>
                    <dd>{formatDateTime(contact.dateSoumission)}</dd>
                  </dl>

                  <div className="devis-drawer-section-title">Demande</div>
                  <div className="devis-drawer-field">
                    <span className="devis-drawer-label">Objet</span>
                    <span>{contact.objetMessage || '—'}</span>
                  </div>
                  <div className="devis-drawer-field">
                    <span className="devis-drawer-label">Message</span>
                    <p className="devis-drawer-message">{contact.message || '—'}</p>
                  </div>
                  {contact.mailPlanification && (
                    <div className="devis-drawer-field">
                      <span className="devis-drawer-label">Mail de planification</span>
                      <p className="devis-drawer-message">{contact.mailPlanification}</p>
                    </div>
                  )}
                </>
              )}

              {/* ---------------- Notes ---------------- */}
              {onglet === 'notes' && (
                <>
                  <p className="contrat-form-note">
                    Utilisées pour rédiger le devis, elles <strong>priment sur le message
                    d’origine</strong>.
                  </p>

                  {devisParti ? (
                    <div className="devis-drawer-empty">
                      Devis envoyé : notes figées.
                      {notesEnregistrees && (
                        <p className="devis-drawer-message contact-notes-figees">
                          {notesEnregistrees}
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Les notes sont le seul champ éditable de la fiche (le reste vient du formulaire public). */}
                      <RemplirParTexte
                        contexte="notes internes d'une demande de devis (échanges, besoins, contraintes du client)"
                        champs={[{ cle: 'notes', libelle: 'Notes' }]}
                        onValeurs={(v) => {
                          // Ajout à la suite : l'IA ne voit pas les notes déjà saisies, les remplacer les effacerait.
                          if (typeof v.notes !== 'string' || !v.notes.trim()) return;
                          setNotes((n) => (n.trim() ? `${n.replace(/\s+$/, '')}\n\n${v.notes}` : v.notes));
                        }}
                      />
                      <textarea
                        className="param-textarea contact-notes"
                        rows={10}
                        placeholder="Ex. : rappelé le 15/09 — le CSE veut deux groupes, budget OPCO validé, éviter les lundis."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        disabled={notesSaving}
                      />
                      <div className="devis-modal-actions">
                        {notesMessage && (
                          <span
                            className={
                              notesMessage.ok
                                ? 'param-form-message param-form-message-success'
                                : 'param-form-message param-form-message-error'
                            }
                          >
                            {notesMessage.texte}
                          </span>
                        )}
                        <button
                          type="button"
                          className="devis-btn devis-btn-sm devis-btn-primary"
                          onClick={sauverNotes}
                          disabled={notesSaving || !notesModifiees}
                          title={notesModifiees ? 'Enregistrer' : 'Aucune modification'}
                        >
                          {notesSaving ? 'Enregistrement' : 'Enregistrer les notes'}
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}

              {/* ---------------- Devis ---------------- */}
              {onglet === 'devis' && (
                <>
                  {devisLoading && <div className="devis-drawer-empty">Chargement du devis...</div>}

                  {!devisLoading && devis && (
                    <div className="devis-drawer-devis">
                      <div className="devis-drawer-devis-top">
                        <span className="devis-drawer-devis-numero">{devis.numero}</span>
                        <span className="devis-status-pill">
                          {STATUT_DEVIS_LABELS[devis.statut] || devis.statut}
                        </span>
                      </div>
                      <div className="devis-drawer-devis-montant">
                        {formatMontant(devis.montant_ht)} HT · {formatMontant(devis.montant_ttc)}{' '}
                        TTC
                        <span className="devis-drawer-devis-lignes">
                          {' '}
                          · {devis.lignes?.length || 0} ligne
                          {(devis.lignes?.length || 0) > 1 ? 's' : ''}
                        </span>
                      </div>
                      {nomPdf && (
                        <div className="devis-piece-jointe">
                          <span className="devis-piece-icone" aria-hidden="true">
                            PDF
                          </span>
                          <span className="devis-piece-nom" title={nomPdf}>
                            {nomPdf}
                          </span>
                          <button
                            type="button"
                            className="devis-btn devis-btn-sm"
                            onClick={voirPdf}
                            title={estimation ? 'Voir le PDF de l’estimation' : 'Voir le PDF du devis'}
                          >
                            <IconeOeil />
                            {estimation ? 'Voir PDF estimation' : 'Voir PDF devis'}
                          </button>
                          <button
                            type="button"
                            className="devis-btn devis-btn-sm"
                            onClick={telechargerPdf}
                            disabled={pdfEnCours}
                            title={estimation ? 'Télécharger le PDF de l’estimation' : 'Télécharger le PDF du devis'}
                          >
                            {pdfEnCours ? 'En cours' : (<><IconeTelecharger />{estimation ? 'Télécharger PDF estimation' : 'Télécharger PDF devis'}</>)}
                          </button>
                        </div>
                      )}

                      <button
                        type="button"
                        className="devis-btn devis-btn-sm"
                        onClick={() => regenerer('devis')}
                        disabled={regenerationEnCours || devis.origine === 'externe'}
                        title={
                          devis.origine === 'externe'
                            ? 'Devis établi hors de l’application : son fichier fait foi, il ne se régénère pas'
                            : 'Refaire le PDF à partir des données actuelles'
                        }
                      >
                        {regenerationEnCours ? 'Régénération' : 'Régénérer le PDF'}
                      </button>

                      {pdfInfo && <div className="devis-drawer-hint">{pdfInfo}</div>}
                      {pdfErreur && <div className="devis-row-erreur">{pdfErreur}</div>}

                      <button
                        type="button"
                        className="devis-btn devis-btn-sm"
                        onClick={onVoirDevis}
                      >
                        Voir / modifier le devis
                      </button>
                    </div>
                  )}

                  {!devisLoading && !devis && (
                    <div className="devis-drawer-devis">
                      <div className="devis-drawer-empty">
                        Aucun devis n&apos;a encore été généré pour cette demande.
                      </div>
                      <button
                        type="button"
                        className="devis-btn devis-btn-sm devis-btn-primary"
                        onClick={onGenererDevis}
                        disabled={generation}
                      >
                        {generation ? 'Génération' : 'Générer le devis'}
                      </button>
                    </div>
                  )}

                  {generationErreur && <div className="devis-row-erreur">{generationErreur}</div>}
                </>
              )}

              {/* ---------------- Contrat ---------------- */}
              {onglet === 'contrat' && (
                <div className="devis-drawer-devis">
                  {!devis && (
                    <div className="devis-drawer-empty">
                      Le contrat naît du devis : il n&apos;y en aura pas tant que le devis
                      n&apos;est pas établi.
                    </div>
                  )}

                  {devis && contratLoading && (
                    <div className="devis-drawer-empty">Chargement du contrat...</div>
                  )}

                  {devis && !contratLoading && contrat && (
                    <>
                      <div className="devis-drawer-devis-top">
                        <span className="devis-drawer-devis-numero">{contrat.numero}</span>
                        <span className="devis-status-pill">
                          Généré le {formatDateTime(contrat.updated_at)}
                        </span>
                      </div>
                      <div className="devis-drawer-devis-montant">
                        {contrat.intitule_action}
                        <span className="devis-drawer-devis-lignes">
                          {' '}
                          · {Number(contrat.duree_jours)} j ({Number(contrat.duree_heures)} h) ·{' '}
                          {contrat.nb_participants ?? '—'} participant
                          {(contrat.nb_participants ?? 0) > 1 ? 's' : ''} ·{' '}
                          {formatMontant(contrat.montant_ttc)} TTC
                        </span>
                      </div>

                      {nomPdfContrat && (
                        <div className="devis-piece-jointe">
                          <span className="devis-piece-icone" aria-hidden="true">
                            PDF
                          </span>
                          <span className="devis-piece-nom" title={nomPdfContrat}>
                            {nomPdfContrat}
                          </span>
                          <button
                            type="button"
                            className="devis-btn devis-btn-sm"
                            onClick={voirContrat}
                          >
                            <IconeOeil />
                            PDF
                          </button>
                          <button
                            type="button"
                            className="devis-btn devis-btn-sm"
                            onClick={telechargerContrat}
                            disabled={contratPdfEnCours}
                          >
                            {contratPdfEnCours ? 'En cours' : (<><IconeTelecharger />PDF</>)}
                          </button>
                        </div>
                      )}

                      <button
                        type="button"
                        className="devis-btn devis-btn-sm"
                        onClick={() => regenerer('contrat')}
                        disabled={regenerationEnCours || contrat.origine === 'externe'}
                        title={
                          contrat.origine === 'externe'
                            ? 'Contrat établi hors de l’application : son fichier fait foi, il ne se régénère pas'
                            : 'Refaire le PDF du contrat à partir des données actuelles'
                        }
                      >
                        {regenerationEnCours ? 'Régénération' : 'Régénérer le PDF'}
                      </button>

                      <button
                        type="button"
                        className="devis-btn devis-btn-sm"
                        onClick={() => setSessionsOuvertes(true)}
                      >
                        Sessions &amp; documents
                      </button>

                      {contrat.signature_organisme_le && (
                        <div className="devis-drawer-hint">
                          Signé pour l&apos;organisme de formation le{' '}
                          {formatDateTime(contrat.signature_organisme_le)}.
                        </div>
                      )}
                    </>
                  )}

                  {devis && !contratLoading && !contrat && devis.signature_le && (
                    <div className="devis-drawer-empty">
                      Devis signé, mais le contrat n’a pas pu être généré. Contactez le support.
                    </div>
                  )}

                  {devis && !contratLoading && !contrat && !devis.signature_le && (
                    <div className="devis-drawer-empty">
                      Le contrat sera généré automatiquement dès que le client aura signé le devis.
                    </div>
                  )}

                  {contratErreur && <div className="devis-row-erreur">{contratErreur}</div>}
                </div>
              )}

              {/* ---------------- Documents ---------------- */}
              {onglet === 'documents' && (
                <>
                  <p className="contrat-form-note">
                    Devis signé, contrat, convention… La nature choisie range le fichier.
                  </p>

                  <div className="contact-depot">
                    <label className="param-field">
                      <span className="param-label">Nature du fichier</span>
                      <SelectRecherche
                        className="param-select"
                        value={depot.categorie}
                        onChange={(e) => setDepot((d) => ({ ...d, categorie: e.target.value }))}
                        disabled={depotEnCours}
                      >
                        {CATEGORIES.map((c) => (
                          <option key={c.valeur} value={c.valeur}>
                            {c.label}
                          </option>
                        ))}
                      </SelectRecherche>
                    </label>
                    <label className="param-field param-field-grow">
                      <span className="param-label">Nom (facultatif)</span>
                      <input
                        type="text"
                        className="param-input"
                        placeholder="À défaut, le nom du fichier"
                        value={depot.libelle}
                        onChange={(e) => setDepot((d) => ({ ...d, libelle: e.target.value }))}
                        disabled={depotEnCours}
                      />
                    </label>
                    <label className="contact-depot-bouton">
                      <span className="devis-btn devis-btn-sm devis-btn-primary">
                        {depotEnCours ? 'Envoi' : 'Choisir un fichier'}
                      </span>
                      <input
                        type="file"
                        accept={FORMATS_ACCEPTES}
                        onChange={deposerFichier}
                        disabled={depotEnCours}
                        hidden
                      />
                    </label>
                  </div>

                  {depotMessage && (
                    <div
                      className={
                        depotMessage.ok
                          ? 'param-form-message param-form-message-success'
                          : 'param-form-message param-form-message-error'
                      }
                    >
                      {depotMessage.texte}
                    </div>
                  )}

                  {docsLoading && <div className="devis-drawer-empty">Chargement des pièces...</div>}

                  {!docsLoading && documents.length === 0 && (
                    <div className="devis-drawer-empty">Aucun fichier dans ce dossier.</div>
                  )}

                  {!docsLoading &&
                    documents.map((d) => (
                      <div className="devis-piece-jointe" key={d.id}>
                        <span className="devis-piece-icone" aria-hidden="true">
                          {(d.nomFichier || '').split('.').pop().slice(0, 4).toUpperCase() || 'DOC'}
                        </span>
                        <span className="devis-piece-nom" title={d.nomFichier}>
                          <NomEditable
                            valeur={d.libelle}
                            edition={renommageId === d.id}
                            onRenommer={(nom) => renommerFichier(d, nom)}
                            onFermer={() => setRenommageId(null)}
                          />
                          <span className="devis-status-detail">
                            {LIBELLE_CATEGORIE[d.categorie] || d.categorie} ·{' '}
                            {formatTaille(d.taille)} · {formatDateTime(d.deposeLe)}
                          </span>
                        </span>
                        <a
                          className="devis-btn devis-btn-sm"
                          href={urlFichierDocument(d.id, { apercu: true })}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <IconeOeil />
                          Voir
                        </a>
                        <button
                          type="button"
                          className="devis-btn devis-btn-sm"
                          onClick={() => setRenommageId(d.id)}
                          disabled={depotEnCours || renommageId === d.id}
                        >
                          Renommer
                        </button>
                        <button
                          type="button"
                          className="devis-btn devis-btn-sm"
                          onClick={() => retirerFichier(d)}
                          disabled={depotEnCours}
                        >
                          Retirer
                        </button>
                      </div>
                    ))}
                </>
              )}

              {/* ---------------- Historique ---------------- */}
              {/* Les emails partis pour cette demande, tels qu'ils ont été
                  reçus : objet, corps, pièces jointes, échecs compris. */}
              {onglet === 'emails' && contact && (
                <EmailsEnvoyes filtres={{ contactId: contact.id }} />
              )}

              {/* Une ligne par changement de statut : qui, quand, quel statut
                  (table historique_demande, migration 093). */}
              {onglet === 'historique' && contact && (
                <HistoriqueStatuts entite="demande" id={contact.id} />
              )}
            </div>
          </>
        )}
      </div>

      {sessionsOuvertes && contrat && (
        <SessionsPanel contrat={contrat} onClose={() => setSessionsOuvertes(false)} />
      )}
    </div>
  );
}

export default ContactDetailPanel;
